import { logger } from "@/services/loggerService";
import { buildAssetDownloadFilename } from "@/utils/assetUtils";

/**
 * Download a set of assets as individual files, one after another.
 *
 * Sequential native browser downloads. Plain <a>.click() — no target, no rel,
 * no blob buffering. Browsers intercept the response when it carries
 * `Content-Disposition: attachment` and trigger a native download dialog with
 * real progress, leaving the current tab in place.
 *
 * REQUIRED: every download URL must respond with Content-Disposition:
 * attachment for the click to download instead of navigating. This is
 * configured via Bunny Edge Rules:
 *   - Stream Pull Zone: matches `*\/original?*` (videos)
 *   - Storage Pull Zone: matches `*download=true*` (images, audio, PDFs)
 * Without those rules in place, the browser will navigate the tab to the first
 * asset's URL and the loop will be torn down with the page.
 *
 * @param {Array<Object>} assets - Assets to download
 * @param {Function} getSource - (asset, 'download') => Promise<{ url }>
 * @param {string} logTag - Log prefix, e.g. '[Assets]'
 * @returns {Promise<{successCount: number, failCount: number}>}
 */
export async function downloadAssetsSequentially(assets, getSource, logTag = "[AssetDownload]") {
  let successCount = 0;
  let failCount = 0;

  for (const asset of assets) {
    try {
      const source = await getSource(asset, "download");
      const downloadUrl = source?.url || null;

      if (!downloadUrl) {
        logger.warn(`${logTag} No download URL available for asset`, {
          assetId: asset.id,
          assetName: asset.name,
        });
        failCount++;
        continue;
      }

      const filename = buildAssetDownloadFilename(asset) || "download";

      logger.info(`${logTag} Triggering download`, { assetId: asset.id, filename });

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      successCount++;

      // Delay between clicks. Tuned to give the browser room for its
      // "multiple downloads" permission prompt and to avoid being rate-limited
      // as a rapid-fire popup attempt.
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } catch (err) {
      failCount++;
      logger.error(`${logTag} Failed to download asset`, {
        assetId: asset.id,
        assetName: asset.name,
        error: err.message,
      });
    }
  }

  return { successCount, failCount };
}
